import { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { LanguageContext } from "../../contexts/LanguageProvider";
import { API_ENDPOINTS } from "../../configs/apiConfig";
import { Card, Button, Stack } from "react-bootstrap";
import ConfigModal from "./ConfigModal";
import { FiEye } from "react-icons/fi";
import { FiPlusCircle, FiFilter, FiGrid, FiSettings } from "react-icons/fi";
import { RiDeleteBin2Line } from "react-icons/ri";
import { Modal } from "react-bootstrap";
import translations from "./translations";
import useMediaQuery from "../../utils/useMediaQuery";
import { useAlertContext } from '../../contexts/AlertContext';
import PaginationComponent from "../PaginationComponent";

function ConfigList() {
  const { language } = useContext(LanguageContext);
  const [configs, setConfigs] = useState([]);
  const [filteredConfigs, setFilteredConfigs] = useState([]);
  const [filterKey, setFilterKey] = useState("");
  const [filterDescription, setFilterDescription] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [configsPerPage, setConfigsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);

  const { showSuccess, showError } = useAlertContext();

  const t = translations[language] || translations.pt;

  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    setConfigsPerPage(isMobile ? 5 : 10);
    setCurrentPage(1);
  }, [isMobile]);

  const fetchConfigs = useCallback(async () => {
    try {
      const auth = localStorage.getItem("auth");
      const response = await axios.get(API_ENDPOINTS.CONFIGS_ALL, {
        headers: {
          Authorization: `Basic ${auth}`,
        },
      });
      setConfigs(response.data);
      setFilteredConfigs(response.data);
    } catch (error) {
      console.error("Error fetching configs:", error);
      showError(t.failedToFetchConfigs);
    }
  }, [showError, t.failedToFetchConfigs]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const applyFilters = () => {
    let filtered = [...configs];
    if (isMobile) {
      setConfigsPerPage(5);
    }

    if (filterKey && filterKey.trim() !== "") {
      filtered = filtered.filter((config) =>
        config.key.toLowerCase().includes(filterKey.toLowerCase())
      );
    }

    if (filterDescription && filterDescription.trim() !== "") {
      filtered = filtered.filter((config) =>
        config.description.toLowerCase().includes(filterDescription.toLowerCase())
      );
    }

    setFilteredConfigs(filtered);
    setCurrentPage(1);
  };

  const handleAddConfig = () => {
    setIsAddMode(true);
    setSelectedConfig(null);
    setIsModalOpen(true);
  };

  const handleEdit = (config) => {
    setIsAddMode(false);
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedConfig(null);
  };

  const handleSaveConfig = async (formData) => {
    try {
      const auth = localStorage.getItem("auth");
      if (isAddMode) {
        const response = await axios.post(
          API_ENDPOINTS.CONFIG_SAVE,
          formData,
          {
            headers: { Authorization: `Basic ${auth}` },
          }
        );
        const newConfig = { ...formData, id: response.data.id || Date.now(), createdAt: new Date().toISOString().slice(0, 10) };
        setConfigs([...configs, newConfig]);
        showSuccess(t.successAddConfig);
        await fetchConfigs();
      } else {
        await axios.put(
          API_ENDPOINTS.CONFIGS_UPDATE(selectedConfig.id),
          formData,
          {
            headers: { Authorization: `Basic ${auth}` },
          }
        );
        setConfigs(
          configs.map((c) =>
            c.id === selectedConfig.id ? { ...formData, id: c.id, createdAt: c.createdAt } : c
          )
        );
        showSuccess(t.successUpdateConfig);
        await fetchConfigs();
      }

      setTimeout(closeModal, 0);
    } catch (error) {
      showError(isAddMode ? t.errorAddConfig : t.errorUpdateConfig);
    }
  };

  const indexOfLastConfig = currentPage * configsPerPage;
  const indexOfFirstConfig = indexOfLastConfig - configsPerPage;
  const currentConfigs = filteredConfigs.slice(
    indexOfFirstConfig,
    indexOfLastConfig
  );
  const totalPages = Math.ceil(filteredConfigs.length / configsPerPage);

  const handleDelete = async (configId) => {
    try {
      const auth = localStorage.getItem("auth");
      await axios.delete(API_ENDPOINTS.CONFIGS_DELETE(configId), {
        headers: { Authorization: `Basic ${auth}` },
      });
      setConfigs(configs.filter((config) => config.id !== configId));
      applyFilters();
      showSuccess(t.successDeleteConfig);
    } catch (error) {
      showError(t.errorDeleteConfig);
    }
    setShowDeleteConfirm(false);
    setConfigToDelete(null);
  };

  const handleDeleteClick = (config) => {
    setConfigToDelete(config);
    setShowDeleteConfirm(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applyFilters();
    }
  };

  return (
    <div className="page-section">
      <Card>
        <Card.Header as="h2">
          <Stack direction="horizontal" gap={3} className="w-100">
            <div className="d-inline-flex align-items-center gap-2"><FiSettings className="text-primary" /> {t.configList}</div>
            <div className="ms-auto">
              <Button variant="success" onClick={handleAddConfig}>
                <FiPlusCircle className="me-1" /> {t.addConfig}
              </Button>
            </div>
          </Stack>
        </Card.Header>

        <Card.Body>
          <Card className="mb-4">
            <Card.Header as="h4">
              <FiFilter className="me-2" /> {t.filters}
            </Card.Header>
            <Card.Body className="p-3">
              <div className="row align-items-end">
                <div className="col-md-4">
                  <label className="form-label">{t.keyFilter}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={filterKey}
                    onChange={(e) => setFilterKey(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t.placeholderKey}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t.descriptionFilter}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={filterDescription}
                    onChange={(e) => setFilterDescription(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t.placeholderDescription}
                  />
                </div>
                <div className="col-md-4 mt-3">
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={applyFilters}
                  >
                    {t.searchButton}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header as="h4">
              <FiGrid className="me-2" /> {t.configs}
            </Card.Header>
            <Card.Body className="p-2">
              <div className="table-responsive">
                <table className="table table-striped mb-0">
                  <thead>
                    <tr>
                      <th>{t.key}</th>
                      {!isMobile && (
                        <>
                          <th>{t.value}</th>
                          <th>{t.description}</th>
                          <th>{t.createdAt}</th>
                        </>
                      )}
                      <th>{t.actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentConfigs.length > 0 ? (
                      currentConfigs.map((config) => (
                        <tr key={config.id}>
                          <td>
                            <div className="fw-bold">{config.key}</div>
                            {isMobile && (
                              <div className="mt-1">
                                <small className="text-muted">{config.value}</small>
                                <br />
                                <hr className="m-1" />
                                <small className="text-muted">{config.description}</small>
                                <br />
                                <hr className="m-1" />
                                <small className="text-muted">{config.createdAt}</small>
                              </div>
                            )}
                          </td>
                          {!isMobile && (
                            <>
                              <td>{config.value}</td>
                              <td>{config.description}</td>
                              <td>{config.createdAt}</td>
                            </>
                          )}
                          <td>
                            <Stack direction={isMobile ? "vertical" : "horizontal"} gap={2}>
                              <Button
                                size="sm"
                                title={t.view}
                                onClick={() => handleEdit(config)}
                                style={{
                                  width: isMobile ? "100%" : "25px",
                                  height: "25px",
                                  padding: "0",
                                  margin: "0",
                                  color: "#fff",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FiEye size={18} />
                                {isMobile && <span className="ms-2">{t.view}</span>}
                              </Button>
                              <Button
                                size="sm"
                                title={t.delete}
                                onClick={() => handleDeleteClick(config)}
                                style={{
                                  width: isMobile ? "100%" : "25px",
                                  height: "25px",
                                  padding: "0",
                                  margin: "0",
                                  color: "#fff",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <RiDeleteBin2Line size={18} />
                                {isMobile && <span className="ms-2">{t.delete}</span>}
                              </Button>
                            </Stack>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={isMobile ? "2" : "5"} className="text-center">
                          {t.noConfigs}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>

          <ConfigModal
            isOpen={isModalOpen}
            onClose={closeModal}
            onSave={handleSaveConfig}
            initialData={selectedConfig}
            translations={t}
            isAddMode={isAddMode}
          />

          {currentConfigs.length > 0 &&
            <PaginationComponent
              totalPages={totalPages}
              isMobile={isMobile}
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              t={t}
              tItemName={t.configs}
              indexOfFirst={indexOfFirstConfig}
              indexOfLast={indexOfLastConfig}
              lengthTotal={filteredConfigs.length}
            />}
        </Card.Body>
      </Card>

      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t.confirmDeleteConfig}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t.confirmDeleteConfigMessage}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            {t.cancelButton}
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDelete(configToDelete.id)}
          >
            {t.delete}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ConfigList;
